import { isValid } from 'date-fns';
import { isEmpty } from 'lodash';
import { v4 as uuid } from 'uuid';

import { findSearchFilter } from '~/components/Shared/Filters/FilterChips/findSearchFilter';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DataFieldType } from '~/enums/DataFieldType';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { FilterViewType } from '~/evifields/src/types/FilterView';
import {
  ApiSearchFilter,
  APISearchSuggestion,
  ExportExcelField,
  SearchDocumentItem,
  SearchDocumentsMeta,
  SearchDocumentsResult,
  SearchFilterRecord,
  SearchFilterType,
} from '~/features/advanced-search/types';
import { createBooleanTextSearchFilter } from '~/features/filters/searchFiltersUtils';
import {
  testIsBooleanTextSearch,
  testIsFilter,
} from '~/features/search/utils/queryUtils';
import {
  PilotSearchQuery,
  toProvisionContains,
} from '~/redux/api/methods/search';
import { pilotV3 } from '~/services/serviceInstances';
import { ColumnSortOrder } from '~/types/Columns';
import { SearchFilter } from '~/types/DashboardV2';
import { Nullable } from '~/types/Nullable';
import {
  BooleanQueryEntity,
  ClauseFilterQueryEntity,
  ClauseMeta,
  EntityQuery,
  FilterQueryEntity,
  MultiSelectMeta,
} from '~/types/OpenSearch';
import { DEFAULT_TABLE_SORT_COLUMN_ORDER } from '~/utils/table';

import { createResource, JsonApiListResponse } from '../ApiTypes';
import { ChartData, CreateChartParams } from '../types/dashboard/charts';
import { toSortParam } from '../utils';

interface ListResponse<T> {
  data: T[];
  links: any; //TBD
  meta: any; //TBD
}

type FilterResponseType = {
  type: 'filter';
  attributes: ApiSearchFilter;
};

type SuggestionsResponseMeta = {
  select_all_count: string;
};

interface SuggestionsParams {
  filterId: FieldId;
  query: string;
}

interface SearchDocumentsParams {
  query: Array<any>;
  filters?: Array<string | number>;
  sortBy?: ColumnSortOrder;
  page: number;
  pageSize: number;
  store_as_recent_search?: boolean;
}

interface SearchDocumentsResponseType {
  type: 'document';
  attributes: SearchDocumentItem;
}

type ExportOptionsResponse = {
  data: {
    attributes: {
      fields: ExportExcelField[];
      clauses: Array<{
        label: string;
        value: string;
      }>;
      tables: Array<{
        label: string;
        value: string;
      }>;
    };
    type: 'excel';
  };
};

type ExportExcelParams = {
  fields: Nullable<string[]>;
  clauses: Nullable<string[]>;
  tables: Nullable<string[]>;
  includeDuplicates: boolean;
  includeContractRecordUniqueId: boolean;
  query: PilotSearchQuery;
};

interface ApiBucket {
  label: string;
  count: number;
}
interface ApiChartAttributes {
  bucketCount: number;
  buckets: ApiBucket[];
}

export const getFilters = async () => {
  const response: ListResponse<FilterResponseType> =
    await pilotV3.get('/search/filters/');
  const list = response?.data
    ?.filter(
      (item) =>
        item.attributes.type !== DataFieldType.REFERENCE &&
        item.attributes.id !== 'document_group_id',
    )
    .map((filterItem) => toSearchFilter(filterItem.attributes));
  list.push(createBooleanTextSearchFilter());

  const filtersRecord: SearchFilterRecord = {};
  list.forEach((searchFilter) => {
    filtersRecord[searchFilter.id] = {
      ...searchFilter,
      operators: searchFilter.operators.map((op) =>
        `${op.cardinality}` === 'infinity'
          ? { ...op, cardinality: Number.POSITIVE_INFINITY }
          : op,
      ),
    };
  });
  return filtersRecord;
};

export const getFilterSuggestions: ({
  filterId,
  query,
}: SuggestionsParams) => Promise<
  JsonApiListResponse<
    'suggestion',
    APISearchSuggestion,
    SuggestionsResponseMeta,
    void
  >
> = ({ filterId, query }) =>
  pilotV3.get(`/search/filters/${filterId}/suggestions/`, {
    params: { query },
  });

export const searchDocuments: (
  params: SearchDocumentsParams,
) => Promise<SearchDocumentsResult> = async ({
  page,
  pageSize,
  sortBy = DEFAULT_TABLE_SORT_COLUMN_ORDER,
  query,
  filters = [],
  store_as_recent_search = true,
}) => {
  const response: ListResponse<SearchDocumentsResponseType> =
    await pilotV3.post(
      '/search/',
      {
        data: {
          type: 'document',
          attributes: {
            query,
            filters,
            store_as_recent_search,
          },
        },
      },
      {
        params: {
          'page[number]': page,
          'page[size]': pageSize,
          sort: toSortParam(sortBy),
        },
      },
    );

  return {
    results: response?.data?.map((searchItem) => searchItem.attributes),
    meta: response?.meta as SearchDocumentsMeta,
  };
};

export const fetchSearchExportOptions: (
  query: PilotSearchQuery,
) => Promise<ExportOptionsResponse> = (query) => {
  return pilotV3.post('/search/export-options/', {
    data: {
      attributes: {
        query,
      },
    },
  });
};

export const exportExcelV2 = ({
  fields = [],
  clauses = [],
  tables = [],
  includeDuplicates,
  includeContractRecordUniqueId,
  query,
}: ExportExcelParams) => {
  return pilotV3.post(
    '/search/export/',
    {
      data: {
        type: 'excel',
        attributes: {
          fields,
          clauses,
          tables,
          include_dup_docs: includeDuplicates,
          include_contract_record_unique_id: includeContractRecordUniqueId,
          query,
        },
      },
    },
    {
      responseType: 'arraybuffer',
      // @ts-ignore RTKQ missing mapping of this property
      fullResponse: true,
    },
  );
};
const toSearchFilter: (apiSearchFilter: ApiSearchFilter) => SearchFilter = (
  apiSearchFilter,
) => {
  const { clause_text_search } = apiSearchFilter;
  const settings = clause_text_search
    ? { textSearchFilter: clause_text_search }
    : {};
  return {
    ...apiSearchFilter,
    settings,
    type: toEviFilterType(apiSearchFilter.type),
  };
};

export const toEviFilterType = (type: SearchFilterType) => {
  switch (type) {
    case DataFieldType.AGE:
      return 'age';
    case DataFieldType.ARRAY_MULTIPLE:
    case 'document_group_id':
      return 'enum_set';
    case DataFieldType.ARRAY_SINGLE:
      return 'enum';
    case DataFieldType.ATTACHMENT:
      return 'file';
    case DataFieldType.BOOLEAN:
      return 'boolean';
    case DataFieldType.CLAUSE:
    case 'clause':
      return 'clause';
    case DataFieldType.DATE:
      return 'date';
    case DataFieldType.NUMBER:
      return 'number';
    case DataFieldType.ADDRESS:
    case DataFieldType.MONETARY:
      return 'text';
    case DataFieldType.REFERENCE:
    case DataFieldType.STRING:
    case DataFieldType.TEXT_AREA:
      return 'text';
    case 'folder':
      return 'folder';
    default:
      return type;
  }
};

export const toSearchV3Filters = (
  query: EntityQuery[],
  searchFilters: SearchFilterRecord,
) => {
  const fieldsInQuery = query.filter(
    (
      queryEntity,
    ): queryEntity is
      | FilterQueryEntity
      | ClauseFilterQueryEntity
      | BooleanQueryEntity =>
      testIsFilter(queryEntity) || testIsBooleanTextSearch(queryEntity),
  );

  return fieldsInQuery.map(
    (field: FilterQueryEntity | ClauseFilterQueryEntity | BooleanQueryEntity) =>
      toSearchV3Filter(field, searchFilters),
  );
};

export const toSearchV3Filter = (
  field: FilterQueryEntity | BooleanQueryEntity,
  searchFilters: SearchFilterRecord,
): Filter => {
  if (testIsBooleanTextSearch(field)) {
    return {
      asyncValue: undefined,
      fieldId: 'bool_text_search',
      fieldType: 'bool_text_search',
      hasError: false,
      operatorId: null,
      id: uuid(),
      values: field.value ? [field.value] : [],
    };
  }
  const fieldAsFilter = field as FilterQueryEntity;
  if (
    fieldAsFilter.id === 'search_bar' &&
    String(fieldAsFilter.operator) === 'bool_text_search'
  ) {
    const query = (fieldAsFilter.value as unknown as { query?: string })?.query;
    return {
      asyncValue: undefined,
      fieldId: 'bool_text_search',
      fieldType: 'bool_text_search',
      hasError: false,
      operatorId: null,
      id: uuid(),
      values: query ? [query] : [],
    };
  }
  const searchFilter = findSearchFilter(Object.values(searchFilters), field.id);
  const type = searchFilter?.type;
  let asyncValue, values: unknown[];
  let filterView: FilterViewType | undefined;
  switch (type) {
    case 'clause_v2':
    case 'clause': {
      // Ask AI builds its handoff query from an LLM, which can omit `value_meta` entirely.
      const meta = field.value.value_meta as ClauseMeta | undefined;
      const cts = meta?.clause_text_search;

      const clauseValue: any = {
        provision: field.value.value_list[0],
        text_search: [],
      };
      if (cts) {
        const ctsOperators =
          (searchFilter?.settings as any)?.textSearchFilter?.operators ?? [];
        const operator = ctsOperators.find((op: any) => op.id === cts.operator);

        clauseValue.text_search.push({
          contains: toProvisionContains(field.operator)!,
          scope: {
            value: cts.operator,
            label: operator?.label ?? '',
          },
          text: cts.value.value_list[0],
        });
      }

      values = field.value?.value_list.length ? [clauseValue] : [];
      break;
    }
    case 'date': {
      const dateArray = field.value.value_list;
      const isAllValidDates = dateArray.every((dateString) =>
        isValid(new Date(dateString as string)),
      );
      const datesValues = isAllValidDates
        ? dateArray.map((dateString) => {
            return parseDate(dateString as string);
          })
        : [
            {
              unit: dateArray[1],
              value: dateArray[0],
            },
          ];
      values = field.value.value_list.length ? datesValues : [];
      break;
    }
    case 'enum_set': {
      const metaData =
        field.value.value_meta?.[
          'multi_select_data' as keyof typeof field.value.value_meta
        ] || null;

      if (!isEmpty(metaData)) {
        asyncValue = {
          isAllSelected: metaData['is_all_selected'],
          length: metaData['length'],
          search: metaData['search'],
          selectedValuesMap: metaData['selected_values_map'],
        };
      } else {
        asyncValue = undefined;
      }
      filterView = (field.value.value_meta as MultiSelectMeta | undefined)
        ?.filterView;
      values = [...field.value.value_list];
      break;
    }
    default: {
      values = [...field.value.value_list];
    }
  }

  // Cast numeric folder IDs to strings to ensure the folder selection is matched correctly.
  // Evisearch reports this field as an enum_set, leaving its ID as the only marker.
  if (type === 'folder' || field.id === 'folder_Document') {
    values = values.map(String);
  }

  return {
    asyncValue,
    fieldId: field.id as string,
    fieldType: type,
    hasError: false,
    id: uuid(),
    filterView,
    operatorId: field.operator,
    values,
  };
};

const createChart: createResource<
  'chart',
  ApiChartAttributes,
  CreateChartParams
> = ({ uuid, query, bucket_size = 10, interval }) =>
  pilotV3.post(`/search/chart/${uuid}/`, {
    data: {
      attributes: {
        query,
        bucket_size,
        interval,
      },
    },
  });

export const getChartData = async (
  params: CreateChartParams,
): Promise<ChartData> => {
  const response = await createChart(params);

  const { buckets: apiBuckets, bucketCount } = response!.data.attributes;
  return {
    bucketCount,
    buckets: apiBuckets.map((b) => ({ label: b.label, value: b.count })),
    meta: response?.meta,
  };
};
